import React, { Component } from "react";
import { Container } from "reactstrap";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import { withRouter } from "react-router-dom";
import FormBuilder from "../../../../toolkit/baseForms2/formBuilder/FormBuilder";
import {
  Inputs,
  Buttons,
  buildFormConfig,
  defaultFiltros
} from "./ReporteGeneralVendedor.form";
import { MemoTable } from "./ReporteGeneralVendedor.table";
import AnualidadSnapshot from "../../../../types/AnualidadSnapshot";
import {
  errorNotification,
  infoNotification
} from "../../../../utils/notifications";
import { Preview } from "../../../../types/IAction";
import { parse } from "json2csv";
import FileDownload from "js-file-download";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";
import { getRelevanciaComision } from "./relevanciaComision";
import { userHasPermission } from "../../../../utils/accessTree";
import permissionCodes from "../../../../utils/permissionCodes";
import s from "./styles.scss";

interface Props {
  userContext: any;
  snapshot: Page<AnualidadSnapshot> | null;
  gettingSnapshot: boolean;
  gettingSnapshotError: string;
  clearAnualidadSnapshot: () => void;
  getReporteGeneralVendedor: (
    periodo: string,
    idVendedor: number | null,
    page?: number,
    size?: number
  ) => Promise<Preview<any>>;
}

interface State {
  downloading: boolean;
  filters: Inputs & {
    periodo: string;
    idVendedor: number | null;
    page: number;
    size: number;
  };
}

/**
 * REPORTE GENERAL DE VENDEDORES — UI (presentational)
 * ---------------------------------------------------------------------------
 * Pantalla: /reportes/reporte-general-vendedor
 *
 * - Filtros: Mes (obligatorio) + Año + Vendedor (visible/obligatorio solo con permiso 57000).
 * - Datos vía Redux: getReporteGeneralVendedor → snapshot en props.
 * - Tabla estilo Gerente General + columna "Relevante para comisión" (cálculo en front).
 * - Export CSV local de la página cargada.
 *
 * BFF: GET /api/comisiones-anualidad/snapshot/reporte-general-vendedor
 */
class ReporteGeneralVendedorComponent extends Component<Props, State> {
  state = {
    downloading: false,
    filters: {
      periodo: null,
      idVendedor: null,
      page: 0,
      size: 100
    } as any
  };

  canFilterBySeller = () =>
    userHasPermission(
      permissionCodes.COMMISSIONS_REPORTE_GENERAL_VENDEDOR_FILTRO
    );

  formConfig = buildFormConfig(this.canFilterBySeller());

  componentDidMount(): void {
    if (this.props.clearAnualidadSnapshot) {
      this.props.clearAnualidadSnapshot();
    }
  }

  /**
   * El backend espera auth user_id (ej. 10784), no número de empleado (ej. 10481).
   */
  toUserId = (raw: any): number | null => {
    if (raw == null || raw === "") {
      return null;
    }
    if (typeof raw === "object") {
      const nested =
        raw.userId != null
          ? raw.userId
          : raw.user_id != null
          ? raw.user_id
          : raw.value != null
          ? raw.value
          : raw.id;
      return this.toUserId(nested);
    }
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) ? n : null;
  };

  /**
   * Con permiso 57000 el vendedor del filtro es obligatorio.
   * Sin permiso, el backend usa el usuario de sesión (no se envía idVendedor).
   */
  resolveIdVendedorParam = (): number | null => {
    if (!this.canFilterBySeller()) {
      return null;
    }
    return this.toUserId(this.state.filters.idVendedor);
  };

  fetchData = async ({ page, pageSize }) => {
    const { filters } = this.state;
    if (!filters.periodo) {
      return;
    }

    const idVendedor = this.resolveIdVendedorParam();
    if (this.canFilterBySeller() && idVendedor == null) {
      errorNotification("El vendedor es obligatorio");
      return;
    }

    const preview = await this.props.getReporteGeneralVendedor(
      filters.periodo,
      idVendedor,
      page,
      pageSize
    );

    if (!preview.fulfilled) {
      errorNotification(
        preview.gettingSnapshotError ||
          "No fue posible consultar el reporte general de vendedores"
      );
    }
  };

  submit = (data: { [key in Inputs]: any }) => {
    if (!data.mes) {
      errorNotification("El periodo es requerido");
      return;
    }

    const idVendedor = this.toUserId(data.idVendedor);
    if (this.canFilterBySeller() && idVendedor == null) {
      errorNotification("El vendedor es obligatorio");
      return;
    }

    const periodo = `${data.anio || new Date().getFullYear()}-${data.mes}-01`;

    this.setState(
      {
        filters: {
          ...data,
          periodo,
          idVendedor,
          page: 0,
          size: 100
        } as any
      },
      () => this.fetchData({ page: 0, pageSize: 100 })
    );
  };

  handleExport = () => {
    const page = this.props.snapshot;
    const rows = (page && page.content) || [];
    if (!rows.length) {
      errorNotification("No hay resultados para descargar");
      return;
    }
    this.exportLocalCsv(rows);
  };

  exportLocalCsv = (rows: AnualidadSnapshot[]) => {
    try {
      infoNotification("Generando reporte...");
      const csvRows = rows.map(row => {
        const { relevante, motivo } = getRelevanciaComision(row);
        return {
          Cotización: row.idCotizacionItem,
          Periodo: row.periodo ? formatIsoDate(row.periodo) : "",
          "Folio Factura": row.folioFactura || "",
          Vendedor: row.vendedor || "",
          Descripción: row.descripcion || "",
          Membresía: `${row.idPublico || ""} de ${row.clubBase || ""}`.trim(),
          "Precio Venta":
            row.precioVenta != null
              ? formatMoney(Number(row.precioVenta), 2)
              : "",
          "Relevante para comisión": relevante ? "Sí" : "No",
          Motivo: motivo
        };
      });
      const csv = parse(csvRows, { quotes: '"' });
      FileDownload(csv, "ReporteGeneralVendedores.csv");
    } catch (error) {
      errorNotification("No se pudo generar el archivo CSV");
    }
  };

  render() {
    const loading = this.props.gettingSnapshot || this.state.downloading;

    return (
      <Container className="mt-3 mb-5">
        <div className={s.filtros}>
          <FormBuilder<Inputs, Buttons>
            config={this.formConfig}
            init={defaultFiltros}
            submit={this.submit}
            processing={loading}
          />
        </div>

        {this.props.snapshot && (
          <MemoTable
            page={this.props.snapshot}
            loading={loading}
            onFetchData={this.fetchData}
            onExport={this.handleExport}
          />
        )}
      </Container>
    );
  }
}

export default withRouter(withStyles(s)(ReporteGeneralVendedorComponent));
